import React, { useState, useContext, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import "./ReportPage.css";
import { IoPrintOutline } from "react-icons/io5";
import { FiEdit } from "react-icons/fi";
import Pagination from "./Pagination";
import axios from "axios";
import Navbar from "./Navbar";
import { LanguageContext } from "./LanguageContext";
import DataView from "./DataView";
import DeletePopup from "./DeletePopup";
import EditPopup from "./EditPopup";
import ConfirmPopup from "./ConfirmPopup";
import CustodyLog from "./CustodyLog";
import "./DataList.css";
import "./new_DataList.css";
import { BsInfoCircle } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import enTranslations from "./translations/en.json";
import guTranslations from "./translations/gu.json";
import hinTranslations from "./translations/hi.json";
import mrTranslations from "./translations/mr.json";

import Tooltip from "@mui/material/Tooltip";
import * as XLSX from "xlsx";
import { MdAttachFile } from "react-icons/md";
import API_URL from "./Config";
import { useCapabilities } from "./contexts/CapabilitiesContext";
import {
  ALL_SCOPE_VALUE,
  REPORT_FILTER_OPTION_STORAGE_KEY,
  REPORT_HIERARCHY_SCOPE_STORAGE_KEY,
  fetchEntryTypeRecords,
  fetchReportRecords,
} from "./services/reportService";

function ReportPage() {
  const { categoryType, categoryName } = useParams();
  const [data, setData] = useState([]);
  const location = useLocation();
  // const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    document.body.classList.add("report-register-page");

    return () => {
      document.body.classList.remove("report-register-page");
    };
  }, []);
  const [userState, setUserState] = useState("");
  const [sortOrder, setSortOrder] = useState("latest");
  const [categoryData, setCategoryData] = useState([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [showPerPage, setShowPerPage] = useState(50);
  const [pagination, setPagination] = useState({
    start: 0,
    end: 50,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { selectedLanguage } = useContext(LanguageContext);
  const { can, capabilities, loading: capabilitiesLoading } = useCapabilities();
  const isCapabilitiesReady = !capabilitiesLoading && capabilities !== null;
  const canReadAdminReports =
    isCapabilitiesReady && can("reports", "read_admin");
  const translations = {
    en: enTranslations,
    gu: guTranslations,
    hi: hinTranslations,
    mr: mrTranslations,
  };
  const locationState = location.state || {};
  const isEntryTypeReport = categoryType === "entry_type";
  const reportTitle = locationState.categoryLabel || categoryName;

  useEffect(() => {
    const state = localStorage.getItem("userState");
    if (state) {
      setUserState(state);
    }
  }, []);
  const sortData = (dataToSort) => {
    return dataToSort.sort((a, b) => {
      switch (sortOrder) {
        case "નવીનતમ":
          return new Date(b.date_of_seizure) - new Date(a.date_of_seizure);
        case "જૂના":
          return new Date(a.date_of_seizure) - new Date(b.date_of_seizure);
        case "AtoZ":
          return a.description.localeCompare(b.description);
        case "ZtoA":
          return b.description.localeCompare(a.description);
        case "તારીખ":
          return new Date(b.date_of_seizure) - new Date(a.date_of_seizure);
        case "સરનો":
          return parseInt(a.serial_number) - parseInt(b.serial_number);
        case "ગુના_નો":
          return parseInt(a.guna_number) - parseInt(b.guna_number);
        case "તારીખ_જપ્ત_કર્યા_ની":
          return new Date(a.date_of_seizure) - new Date(b.date_of_seizure);
        case "મુદ્દામાલ_પાવતી_નો":
          return parseInt(a.exhibit_number) - parseInt(b.exhibit_number);
        case "જથ્થો":
          return parseInt(a.quantity) - parseInt(b.quantity);
        case "કિંમત":
          return parseInt(a.price) - parseInt(b.price);
        default:
          return 0;
      }
    });
  };
  const translatedText = (key) => translations[selectedLanguage][key] || key;

  const getStoredScope = () => {
    const storedScope = localStorage.getItem(REPORT_HIERARCHY_SCOPE_STORAGE_KEY);

    if (storedScope) {
      try {
        const parsedScope = JSON.parse(storedScope);

        return {
          districtId:
            parsedScope?.districtId === ALL_SCOPE_VALUE
              ? undefined
              : parsedScope?.districtId,
          subDivisionId:
            parsedScope?.subDivisionId === ALL_SCOPE_VALUE
              ? undefined
              : parsedScope?.subDivisionId,
          stationId:
            parsedScope?.stationId === ALL_SCOPE_VALUE
              ? undefined
              : parsedScope?.stationId,
          userIds: parsedScope?.userId ? [parsedScope.userId] : [],
        };
      } catch (error) {
        console.error("Failed to parse stored report scope:", error);
      }
    }

    return {};
  };

  const getReportFilters = () => ({
    filterOption:
      locationState.filterOption ||
      localStorage.getItem(REPORT_FILTER_OPTION_STORAGE_KEY) ||
      "",
    startDate: locationState.startDate || "",
    endDate: locationState.endDate || "",
  });

  useEffect(() => {
    let isMounted = true;

    const loadCategoryData = async () => {
      if (!isCapabilitiesReady) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const scope = locationState.scope || getStoredScope();
        const filters = getReportFilters();
        const entryTypeCode =
          locationState.entryTypeCode ||
          (decodeURIComponent(categoryName || "") === "none"
            ? "none"
            : decodeURIComponent(categoryName || ""));
        const categoryFilter = locationState.categoryFilter || "";
        const page = Math.floor(pagination.start / showPerPage) + 1;
        const nextData = isEntryTypeReport
          ? await fetchEntryTypeRecords({
              category: categoryFilter,
              entryType: entryTypeCode,
              filters,
              lang: selectedLanguage,
              page,
              pageSize: showPerPage,
              scope,
              search: searchQuery,
            })
          : await fetchReportRecords({
              canReadAdmin: canReadAdminReports,
              categoryName,
              categoryType,
              filters,
              scope,
            });

        if (!isMounted) {
          return;
        }

        setCategoryData(
          isEntryTypeReport
            ? Array.isArray(nextData?.data)
              ? nextData.data
              : []
            : Array.isArray(nextData)
            ? nextData
            : []
        );
        setServerTotal(
          isEntryTypeReport ? Number(nextData?.total) || 0 : 0
        );
      } catch (apiError) {
        if (!isMounted) {
          return;
        }

        setCategoryData([]);
        setServerTotal(0);
        setError(
          apiError?.response?.data?.message ||
            apiError?.message ||
            "Error fetching category data"
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadCategoryData();

    return () => {
      isMounted = false;
    };
  }, [
    canReadAdminReports,
    categoryName,
    categoryType,
    isEntryTypeReport,
    isCapabilitiesReady,
    locationState.categoryFilter,
    locationState.endDate,
    locationState.entryTypeCode,
    locationState.filterOption,
    locationState.scope,
    locationState.startDate,
    pagination.start,
    searchQuery,
    selectedLanguage,
    showPerPage,
  ]);

  const handleAttachmentIconClick = (id) => {
    navigate(`/attachment/${id}`, {
      state: {
        from: {
          pathname: `${location.pathname}${location.search}`,
          state: location.state ?? null,
        },
      },
    });
  };

  const onPaginationChange = (start, end) => {
    if (pagination.start !== start || pagination.end !== end) {
      setPagination({ start: start, end: end });
    }
  };

  const handleShowPerPageChange = (e) => {
    const selectedShowPerPage = parseInt(e.target.value, 10);
    setShowPerPage(selectedShowPerPage);
    setPagination({ start: 0, end: selectedShowPerPage });
  };

  // const handleDeleteClick = (id) => {
  //   setDeleteItemId(id);
  //   setShowDeleteAlert(true);
  // };

  const handleView = (id) => {
    const selectedDataRow = categoryData.find((item) => item.id === id);

    if (selectedDataRow) {
      setSelectedData(selectedDataRow);
      setShowPopup(true);
      setShowEditForm(false);
    } else {
      console.error(`Item with id ${id} not found.`);
    }
  };

  const handleEditClick = (item) => {
    setSelectedData({
      ...item,
    });
    setShowPopup(false);
    setShowEditForm(true);
  };

  const handleClosePopup = () => {
    setSelectedData(null);
    setShowPopup(false);
  };

  const [deleteItemId, setDeleteItemId] = useState(null);
  const [selectedData, setSelectedData] = useState(null);

  const [showPopup, setShowPopup] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [searchText, setSearchText] = useState("");
  // const [filteredData, setFilteredData] = useState([]);
  const [showDeleteSuccessAlert, setShowDeleteSuccessAlert] = useState(false);
  const navigate = useNavigate();

  const handleConfirmDelete = () => {
    handleDelete(deleteItemId);
    setShowDeleteAlert(false);
    setShowDeleteSuccessAlert(true); // Show "popup3"
  };

  const handleDelete = async (id) => {
    try {
      const authKey = localStorage.getItem("authKey");
      await axios.delete(`${API_URL}/api/data/${id}`, {
        headers: {
          Authorization: authKey,
        },
      });

      // Update the categoryData state by removing the deleted item
      setCategoryData((prevData) => prevData.filter((item) => item.id !== id));

      setShowDeleteSuccessAlert(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCloseDeleteSuccessAlert = () => {
    setShowDeleteSuccessAlert(false);
  };

  async function fetchCategoryData(categoryType, categoryName) {
    try {
      if (!isCapabilitiesReady) {
        return;
      }

      if (canReadAdminReports) {
        return;
      }
      const nextData = await fetchReportRecords({
        canReadAdmin: false,
        categoryName,
        categoryType,
      });
      setCategoryData(Array.isArray(nextData) ? nextData : []);
      setError(null);
    } catch (error) {
      setError("Error fetching category data");
    } finally {
      setIsLoading(false);
    }
  }

  // Modify your handleEditSubmit function
  const handleEditSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission
    try {
      const authKey = localStorage.getItem("authKey");
      await axios.put(` ${API_URL}/api/data/${selectedData.id}`, selectedData, {
        headers: {
          Authorization: authKey,
        },
      });

      // After successfully editing, fetch the data for the new category
      fetchCategoryData(categoryType, categoryName);

      setSelectedData(null); // Clear selectedData after edit
      setShowEditForm(false);
    } catch (error) {
      console.error("Error updating data:", error);
    }
  };

  // const [data, setData] = useState([]);
  const handleDeleteClick = (id) => {
    setShowDeleteAlert(true);
    setShowDeleteSuccessAlert(false); // Set this to false when delete is clicked
    setDeleteItemId(id);
  };

  // function formatDate(inputDate) {
  //   const options = {
  //     day: "2-digit",
  //     month: "2-digit",
  //     year: "numeric",
  //     hour: "2-digit",
  //     minute: "2-digit",
  //     hour12: false,
  //     timeZone: "UTC",
  //   };

  //   const date = new Date(inputDate);
  //   const formattedDate = date.toLocaleDateString("en-US", options);
  //   return formattedDate;
  // }

  // function formatDates(inputDate) {
  //   const options = {
  //     day: "2-digit",
  //     month: "2-digit",
  //     year: "numeric",
  //   };

  //   const date = new Date(inputDate);
  //   const formattedDate = date.toLocaleDateString("en-US", options);
  //   return formattedDate;
  // }
  // const formatDate = (inputDate) => {
  //   const date = new Date(inputDate);
  //   const formatter = new Intl.DateTimeFormat("en-GB", {
  //     day: "2-digit",
  //     month: "2-digit",
  //     year: "numeric",
  //   });
  //   return formatter.format(date);
  // };
  // const formatDates = (inputDate) => {
  //   const date = new Date(inputDate);
  //   const formatter = new Intl.DateTimeFormat("en-GB", {
  //     day: "2-digit",
  //     month: "2-digit",
  //     year: "numeric",
  //     hour: "2-digit",
  //     minute: "2-digit",
  //     hour12: false,
  //     timeZone: "UTC",
  //   });
  //   return formatter.format(date);
  // };
  const handleButtonClick = (id) => {
    // Navigate to a new screen or page with the specific id
    navigate(`/new-screen/${id}`);
  };

  function formatDate(inputDate) {
    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };

    const date = new Date(inputDate);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();

    const formattedDate = `${day}/${month}/${year}`;
    return formattedDate;
  }

  function formatDates(inputDate) {
    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC",
    };

    const date = new Date(inputDate);

    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getUTCFullYear();

    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");

    const formattedDate = `${day}/${month}/${year} ${hours}:${minutes}`;
    return formattedDate;
  }
  const handleExportToXlsx = () => {
    if (categoryData.length === 0) {
      return; // No data to export
    }

    // Get column headers from the keys of the first data item
    const exportColumns = Object.keys(categoryData[0]);
    const filteredData = categoryData.map((item) =>
      exportColumns.reduce((obj, key) => {
        obj[key] = item[key];
        return obj;
      }, {})
    );

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

    XLSX.writeFile(workbook, `${categoryName}.xlsx`);
  };

  const safeIncludes = (value, search) =>
    String(value ?? "")
      .toLowerCase()
      .includes(String(search ?? "").toLowerCase());

  const filteredData = Array.isArray(categoryData)
    ? categoryData.filter(
        (item) =>
          safeIncludes(item.guna_number, searchQuery) ||
          safeIncludes(item.exhibit_number, searchQuery) ||
          safeIncludes(item.date_of_seizure, searchQuery) ||
          safeIncludes(item.muddamaal_type, searchQuery) ||
          safeIncludes(item.vehicle_reason, searchQuery) ||
          safeIncludes(item.quantity, searchQuery) ||
          safeIncludes(item.price, searchQuery) ||
          safeIncludes(item.status_of_muddamaal, searchQuery) ||
          safeIncludes(item.description, searchQuery)
      )
    : [];
  const visibleData = isEntryTypeReport
    ? categoryData
    : filteredData.slice(pagination.start, pagination.end);
  const paginationTotal = isEntryTypeReport
    ? serverTotal
    : filteredData.length;
  const paginationResetKey = isEntryTypeReport
    ? `${categoryType}-${categoryName}-${searchQuery}-${showPerPage}`
    : `${categoryType}-${categoryName}`;

  return (
    <>
      <Navbar />
      <div className="datalist-main-container report-data-page datalist-fit-layout new_datalist-layout">
        <div className="datalist-main-container-header new_datalist-header report-page">
          <div className="datalist-left-side-header new_datalist-header-group new_datalist-header-group--left">
            <div className="report-page__title-shell">
              <h3 className="report-page__title">{reportTitle}</h3>
            </div>
            <div className="search-bar new_datalist-search">
              <div className="input-container">
                <FiSearch
                  aria-hidden="true"
                  className="new_datalist-search-icon"
                />
                <input
                  type="text"
                  placeholder="Ex. Search by FIR No."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="datalist-right-side-header new_datalist-header-group new_datalist-header-group--right">
            <div className="export-btn new_datalist-secondary-action">
              <button onClick={handleExportToXlsx} type="button">
                Export
              </button>
            </div>
          </div>
        </div>

        <div className="datalist-main-container-body new_datalist-body">
          <div className="datalist-scroll-body new_datalist-scroll-body">
            <div className="data-list-table container50 new_datalist-table-shell">
              <table className="tables new_datalist-table">
                <thead className="data-list-table-header new_datalist-table-head">
                  <tr>
                    {/* <th className="page-sticky-col page-sticky-col--start"> */}
                    <th>
                      Sr.No
                    </th>

                    {userState === "Andaman and Nicobar" && (
                      <th>{translatedText("exhibit_number")}</th>
                    )}

                    <th>{translatedText("guna_number")}</th>

                    <th>{translatedText("date_of_seizure")}</th>
                    {userState === "Gujarat" && (
                      <th>{translatedText("exhibit_number")}</th>
                    )}
                    <th
                      className={
                        isEntryTypeReport
                          ? "report-page__entry-hidden"
                          : undefined
                      }
                    >
                      {translatedText("muddamaal_type")}
                    </th>
                    <th>{translatedText("unit")}</th>
                    <th>{translatedText("quantity")}</th>
                    {userState === "Gujarat" && (
                      <th>{translatedText("price")}</th>
                    )}
                    <th>{translatedText("status_of_muddamaal")}</th>
                    <th>{translatedText("updated_on")}</th>
                    <th>{translatedText("act_section_of_low")}</th>
                    <th>{translatedText("description")}</th>
                    <th>{translatedText("reason_for_pendency")}</th>
                    <th>{translatedText("attachment")}</th>
                    <th className="page-sticky-col page-sticky-col--end">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="data-list-table-body new_datalist-table-body">
                  {isLoading ? (
                    <tr>
                      <td colSpan="11">Loading...</td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="11">Error: {error}</td>
                    </tr>
                  ) : categoryData && categoryData.length === 0 ? (
                    <tr>
                      <td colSpan="11">No data available</td>
                    </tr>
                  ) : (
                    visibleData
                      .map((item, index) => (
                        <tr key={item.id}>
                          <td className="clm1 page-sticky-cell page-sticky-cell--start">
                            {item.serial_number || "NA"}
                          </td>

                          {userState === "Andaman and Nicobar" && (
                            <td className="clm4">
                              {item.exhibit_number || "NA"}
                            </td>
                          )}

                          <td className="clm2">{item.guna_number || "NA"}</td>
                          <td className="clm3">
                            {formatDate(item.date_of_seizure || "NA")}
                          </td>
                          {userState === "Gujarat" && (
                            <td className="clm4">
                              {item.exhibit_number || "NA"}
                            </td>
                          )}
                          <td
                            className={`${
                              isEntryTypeReport
                                ? "report-page__entry-hidden "
                                : ""
                            }clm5 ${
                              item.muddamaal_type === "અન્ય" ||
                              item.muddamaal_type === "Others"
                                ? "bg-color-1"
                                : item.muddamaal_type ===
                                    "ડ્રગ્સ & નાર્કોટિક્સ" ||
                                  item.muddamaal_type === "Drugs & Narcotics"
                                ? "bg-color-2"
                                : item.muddamaal_type === "સાંસ્કૃતિક મિલકત" ||
                                  item.muddamaal_type === "Cultural Heritage"
                                ? "bg-color-3"
                                : item.muddamaal_type === "ચલણી નાણું" ||
                                  item.muddamaal_type === "Counterfeit Currency"
                                ? "bg-color-4"
                                : item.muddamaal_type === "અગ્નિ હથિયાર" ||
                                  item.muddamaal_type === "Firearms"
                                ? "bg-color-5"
                                : item.muddamaal_type === "અન્ય હથિયાર" ||
                                  item.muddamaal_type === "Other Weapons"
                                ? "bg-color-6"
                                : item.muddamaal_type === "પ્રોહિબીશન" ||
                                  item.muddamaal_type === "Prohibition"
                                ? "bg-color-7"
                                : item.muddamaal_type === "સોનું" ||
                                  item.muddamaal_type === "Gold"
                                ? "bg-color-8"
                                : item.muddamaal_type === "ઘરેણાં" ||
                                  item.muddamaal_type === "Jewellery"
                                ? "bg-color-9"
                                : item.muddamaal_type ===
                                    "બિનવારસી મુદ્દામાલ" ||
                                  item.muddamaal_type === "Unclaimed Property"
                                ? "bg-color-10"
                                : item.muddamaal_type === "બનાવટી ચલણી નાણું" ||
                                  item.muddamaal_type === "Counterfeit Currency"
                                ? "bg-color-11"
                                : item.muddamaal_type ===
                                    "મોબાઈલ અને ઇલેક્ટ્રોનિક ઇકવિપમેન્ટ" ||
                                  item.muddamaal_type ===
                                    "Mobile-Electronic Equipment"
                                ? "bg-color-12"
                                : item.muddamaal_type === "IMFL" ||
                                  item.muddamaal_type === "IMFL"
                                ? "bg-color-6"
                                : item.muddamaal_type ===
                                    "Gold and Jewellery" ||
                                  item.muddamaal_type === "Gold and Jewellery"
                                ? "bg-color-3"
                                : item.muddamaal_type === "Illicit liquor" ||
                                  item.muddamaal_type === "Illicit liquor"
                                ? "bg-color-4"
                                : item.muddamaal_type === "Handia" ||
                                  item.muddamaal_type === "Handia"
                                ? "bg-color-5"
                                : item.muddamaal_type === "વાહન" ||
                                  item.muddamaal_type === "Vehicle"
                                ? "bg-color-133"
                                : ""
                            } ${
                              item.muddamaal_type === "વાહન" ||
                              item.muddamaal_type === "Vehicle"
                                ? "bg-color-black"
                                : ""
                            } ${
                              item.muddamaal_type === "વાહન" ||
                              (item.muddamaal_type === "Vehicle" &&
                                item.muddamaal_type === "વાહન") ||
                              (item.muddamaal_type === "Vehicle" &&
                                item.vehicle_reason === "ડીટેઇન્ડ") ||
                              item.vehicle_reason === "Detained"
                                ? "yellow"
                                : item.muddamaal_type === "વાહન" ||
                                  (item.muddamaal_type === "Vehicle" &&
                                    item.vehicle_reason === "ગુના કામે") ||
                                  item.vehicle_reason === "Gun Kaame"
                                ? "red"
                                : item.muddamaal_type === "વાહન" ||
                                  (item.muddamaal_type === "Vehicle" &&
                                    item.vehicle_reason ===
                                      "સ્ટે. ડા. માં નોંધ આધારે") ||
                                  item.vehicle_reason ===
                                    "Noted on the basis of St. Dr."
                                ? "green"
                                : item.muddamaal_type === "વાહન" ||
                                  (item.muddamaal_type === "Vehicle" &&
                                    item.vehicle_reason === "બિનવારસી") ||
                                  item.vehicle_reason === "Unclaimed"
                                ? "blue"
                                : item.muddamaal_type === "વાહન" ||
                                  (item.muddamaal_type === "Vehicle" &&
                                    item.vehicle_reason === "અન્ય") ||
                                  item.vehicle_reason === "Others"
                                ? "orange"
                                : item.muddamaal_type === "વાહન" ||
                                  (item.muddamaal_type === "Vehicle" &&
                                    item.vehicle_reason ===
                                      "કોગ્નિઝેબલ ગુનો કરવા માટે વપરાતું વાહન") ||
                                  item.vehicle_reason ===
                                    "Vehicle used for committing Cognizable Offence"
                                ? "bitemn"
                                : item.muddamaal_type === "વાહન" ||
                                  (item.muddamaal_type === "Vehicle" &&
                                    item.vehicle_reason ===
                                      "નશામાં ડ્રાઇવિંગ") ||
                                  item.vehicle_reason === "Druken Driving"
                                ? "deeppink"
                                : item.muddamaal_type === "વાહન" ||
                                  (item.muddamaal_type === "Vehicle" &&
                                    item.vehicle_reason ===
                                      "અન્ય વિશેષ અને સ્થાનિક કાયદાઓ હેઠળ આચરવામાં આવેલ કોઈપણ અન્ય ગુનો") ||
                                  item.vehicle_reason ===
                                    "Any other offence committed under other Special &  Local Laws"
                                ? "lightseagreen"
                                : ""
                            } `}
                          >
                            {item.muddamaal_type === "વાહન" ||
                            item.muddamaal_type === "Vehicle" ? (
                              <span>
                                <span
                                  style={{
                                    color: "#003005",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  {item.muddamaal_type || "NA"}
                                </span>
                                <span
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  ({item.vehicle_reason || "NA"})
                                </span>
                              </span>
                            ) : (
                              <span
                                style={{
                                  color: "#003005",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                }}
                              >
                                {item.muddamaal_type || "NA"}
                              </span>
                            )}
                          </td>
                          <td className="clm6">{item.unit || "NA"}</td>
                          <td className="clm6">{item.quantity || "NA"}</td>
                          {userState === "Gujarat" && (
                            <td className="clm7">{item.price || "NA"}</td>
                          )}
                          <td className="clm8">
                            <div className="status-button">
                              <div className="status-text">
                                {item.status_of_muddamaal}
                              </div>
                              <button
                                className="custody-logs"
                                onClick={() => handleButtonClick(item.id)}
                              >
                                Custody Log
                              </button>

                              {isPopupOpen && <CustodyLog />}
                            </div>
                          </td>
                          <td className="clm9">
                            {formatDates(item.created_at)}
                          </td>
                          {/*                      
                      <td className="clm">{item.act || "NA"}</td>
                      <td className="clm">{item.specify_act || "NA" }</td>
                      <td className="clm">{item.section_of_law || "NA"}</td> */}

                          <td
                            className="clm0"
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.section_of_law ? (
                              <Tooltip
                                title={(() => {
                                  try {
                                    const parsedLaw = JSON.parse(
                                      item.section_of_law
                                    );
                                    const lawEntries =
                                      Object.entries(parsedLaw);

                                    return (
                                      <table className="tooltip-table">
                                        <thead>
                                          <tr>
                                            <th className="acts-data">Acts</th>
                                            <th className="laws-data">
                                              Section of Laws
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {lawEntries.map(
                                            ([lawKey, lawValue], index) => (
                                              <tr key={index}>
                                                <td>
                                                  {lawKey === "Others Acts"
                                                    ? `Others Acts (${item.specify_act})`
                                                    : lawKey || "NA"}
                                                </td>
                                                <td>{lawValue || "NA"}</td>
                                              </tr>
                                            )
                                          )}
                                        </tbody>
                                      </table>
                                    );
                                  } catch (error) {
                                    console.error(
                                      "Error parsing section_of_law:",
                                      error
                                    );
                                    return "NA";
                                  }
                                })()}
                                arrow
                              >
                                <span>
                                  view
                                  <BsInfoCircle className="person-icon" />
                                </span>
                              </Tooltip>
                            ) : (
                              "NA"
                            )}
                          </td>

                          <td className="clm10">
                            <span className="report-table__clamp">
                              {item.description || "NA"}
                            </span>
                          </td>
                          <td className="clm10">
                            <span className="report-table__clamp report-table__clamp--reason">
                              {item.pendency_reason || "NA"}
                            </span>
                          </td>
                          <td className="clm1">
                            <span
                              className="inquiry-file-btn"
                              onClick={() => handleAttachmentIconClick(item.id)}
                            >
                              <MdAttachFile className="inquiry-file-icon" />
                            </span>
                          </td>
                          <td className="clm11 page-sticky-cell page-sticky-cell--end">
                            <div className="action-button">
                              <div
                                className="popup-button print-datalist"
                                onClick={() => handleView(item.id)}
                              >
                                {/* <HiOutlinePrinter className="view-btn" /> */}
                                <IoPrintOutline className="view-btn" />
                                <div className="popup-index">Print</div>
                              </div>
                              {!canReadAdminReports && !isEntryTypeReport && (
                                <div
                                  className="popup-button edit-datalist"
                                  onClick={() => handleEditClick(item)}
                                >
                                  {/* <BiEdit className="edit-btn" /> */}
                                  <FiEdit className="edit-btn" />
                                  <div className="popup-index">Edit</div>
                                </div>
                              )}
                              {/* <div
                                className="popup-button delete-datalist"
                                onClick={() => handleDeleteClick(item.id)}
                              >
                              
                                <MdDeleteOutline className="delete-btn" />
                                <div className="popup-index">Delete</div>
                              </div> */}
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <Pagination
        className="pagination--compact new_datalist-pagination"
        resetKey={paginationResetKey}
        showPerPage={showPerPage}
        total={paginationTotal}
        onPaginationChange={onPaginationChange}
        handleShowPerPageChange={handleShowPerPageChange}
      />


      {showPopup && (
        <div className="data-view-popup">
          <DataView
            data={selectedData}
            qrCodeUrl={`${API_URL}/api/data/${selectedData.id}/qr`}
            onClose={handleClosePopup}
          />
        </div>
      )}

      {showEditForm && selectedData && (
        <div className="popup4s">
          <div className="popup-content4s">
            <EditPopup
              showEditForm={showEditForm}
              selectedData={selectedData}
              handleEditSubmit={handleEditSubmit}
              setShowEditForm={setShowEditForm}
              setSelectedData={setSelectedData}
              fetchCategoryData={fetchCategoryData}
              categoryType={categoryType}
              categoryName={categoryType}
            />
          </div>
        </div>
      )}

      {showDeleteAlert && (
        <div className="popup2">
          <div className="popup-content2">
            <DeletePopup
              showDeleteAlert={showDeleteAlert}
              setShowDeleteAlert={setShowDeleteAlert}
              handleConfirmDelete={handleConfirmDelete}
            />
          </div>
        </div>
      )}

      {showDeleteSuccessAlert && (
        <div className="popup3">
          <div className="popup-content3">
            <ConfirmPopup
              handleCloseDeleteSuccessAlert={handleCloseDeleteSuccessAlert}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default ReportPage;
